import { useState } from 'react';
import Layout from 'antd/lib/layout';
import { CustomizableTableContextProvider } from 'src/components/table/CustomizableTableContext';
import { PageFooter } from 'src/components/PageFooter';
import { PageHeader } from '../../components/PageHeader';
import { WithEmptyDataTable } from 'src/components/table/withEmptyDataTable';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import { IPaginationMeta } from 'src/domain/IPagination';
import { ICustomerUtilityBill } from 'src/domain/customer-utility-bill/interface';
import { useCustomerUtilityBillsQuery, useCustomerUtilityBillUtilitiesQuery } from 'src/domain/customer-utility-bill/queries';
import { EditBillModal } from 'src/components/customer-utility-bill/edit-bill/EditBillModal';
import { BillBatchAction, BillsBatchActionModal } from 'src/components/customer-utility-bill/BillsBatchActionModal';
import { AuditTrailModal } from 'src/components/modals/AuditTrailModal/AuditTrailModal';
import { AuditEntityType } from 'src/domain/audit-trail/interface';
import { CustomerUtilityBillSidebar } from 'src/components/customer-utility-bill/sidebar/CustomerUtilityBillSidebar';
import { CustomerUtilityBillHeaderActions } from 'src/components/customer-utility-bill/CustomerUtilityBillHeaderActions';
import { BillsTable } from 'src/components/customer-utility-bill/table/BillsTable';
import { useCustomerUtilityBillPageState } from '../../components/customer-utility-bill/table/useCustomerUtilityBillPageState';
import { useBillActions } from '../../components/customer-utility-bill/table/useBillActions';
import { useBillColumns } from '../../components/customer-utility-bill/table/billColumns';
import { CustomerUtilityBillViewSwitcher } from './customerUtilityBillViewInterface';
import './customerUtilityBill.css';

export const CustomerUtilityBill = () => {
    useDocumentTitle('Customer Utility Bills');

    const [editingRow, setEditingRow] = useState<ICustomerUtilityBill | null>(null);
    const [auditRow, setAuditRow] = useState<ICustomerUtilityBill | null>(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [batchAction, setBatchAction] = useState<BillBatchAction | null>(null);

    const { filters, search, pagination, sorter, query, setFilters, clearFilters, setSearch, onTableChange } = useCustomerUtilityBillPageState();
    const {
        selectedRowKeys,
        selectedRows,
        onSelectionChange,
        clearSelection,
        verify,
        unverify,
        reparse,
        save,
        verifySelected,
        unverifySelected,
        reparseSelected,
        isSaving,
        isVerifying,
        isUnverifying,
        isReparsing,
    } = useBillActions();

    const { data, isFetching, isLoading } = useCustomerUtilityBillsQuery(query);
    const meta: IPaginationMeta | undefined = data?.meta;
    const rows = data?.data ?? [];

    // Utility filter options come from the distinct utilities present on the bills (server-derived).
    const { data: utilities = [] } = useCustomerUtilityBillUtilitiesQuery();
    const utilityOptions = utilities.map(utility => ({ value: utility, label: utility }));

    // Bulk action toggles direction: when every selected row is already verified the button un-verifies, else it verifies.
    // `selectedRows` carries the full records (incl. rows selected on other pages), so this stays correct across pages.
    const allSelectedVerified = selectedRows.length > 0 && selectedRows.every(r => !!r.verifiedAt);

    const columns = useBillColumns({ sorter, onEdit: setEditingRow, onVerify: verify, onUnverify: unverify, onAudit: setAuditRow });

    // Keep the modal open (button shows a spinner) until the update resolves, then close — so the operator
    // gets immediate feedback instead of the modal vanishing into a silent gap before the list refetch.
    const handleSave = (updated: ICustomerUtilityBill, verified: boolean) => {
        save(updated, verified, () => setEditingRow(null));
    };

    // Each runner resolves with the accepted ids (useBillActions already handles notifications/selection);
    // the modal uses that to decide whether to close or switch into live-tracking mode.
    const batchActionRunners: Record<BillBatchAction, () => Promise<string[]>> = {
        verify: verifySelected,
        unverify: unverifySelected,
        reparse: reparseSelected,
    };
    const runBatchAction = (): Promise<string[]> => (batchAction ? batchActionRunners[batchAction]() : Promise.resolve([]));

    const layoutPaddingLeft = sidebarCollapsed ? '35px' : '200px';

    return (
        <CustomizableTableContextProvider configKey="customerBillReports" columns={columns}>
            <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                <CustomerUtilityBillSidebar
                    collapsed={sidebarCollapsed}
                    onCollapseChange={setSidebarCollapsed}
                    filters={filters}
                    onChange={setFilters}
                    onClearFilters={clearFilters}
                    totalRecords={meta?.total ?? 0}
                    utilityOptions={utilityOptions}
                />

                <Layout.Content style={{ padding: '0 26px' }}>
                    <PageHeader
                        pageTitle="Customer Utility Bills"
                        extra={<CustomerUtilityBillViewSwitcher />}
                        actions={
                            <CustomerUtilityBillHeaderActions
                                search={search}
                                selectedCount={selectedRowKeys.length}
                                allSelectedVerified={allSelectedVerified}
                                verifying={isVerifying}
                                unverifying={isUnverifying}
                                reparsing={isReparsing}
                                query={query}
                                onSearch={setSearch}
                                onVerifySelected={() => setBatchAction('verify')}
                                onUnverifySelected={() => setBatchAction('unverify')}
                                onReparseSelected={() => setBatchAction('reparse')}
                                onClearSelected={clearSelection}
                            />
                        }
                    />

                    <WithEmptyDataTable condition={isLoading || isFetching}>
                        <BillsTable
                            dataSource={rows}
                            loading={isLoading || isFetching}
                            meta={meta}
                            pagination={pagination}
                            onChange={onTableChange}
                            selectedRowKeys={selectedRowKeys}
                            onSelectionChange={onSelectionChange}
                        />
                    </WithEmptyDataTable>

                    {editingRow && (
                        <EditBillModal
                            row={editingRow}
                            saving={isSaving}
                            onClose={() => setEditingRow(null)}
                            onSave={handleSave}
                            onReparse={reparse}
                        />
                    )}
                    {auditRow && (
                        <AuditTrailModal
                            entityId={auditRow.id}
                            entityType={AuditEntityType.CUSTOMER_UTILITY_BILL}
                            onClose={() => setAuditRow(null)}
                        />
                    )}
                    {batchAction && (
                        <BillsBatchActionModal
                            action={batchAction}
                            rows={selectedRows}
                            onConfirm={runBatchAction}
                            onClose={() => setBatchAction(null)}
                        />
                    )}

                    <PageFooter />
                </Layout.Content>
            </Layout>
        </CustomizableTableContextProvider>
    );
};
